import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { BonusApi } from '../apis/bonus.api';
import { AppService } from '../../app.service';
import { LocaleService } from './locale.service';
import { LocalStorageService } from './localstorage.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class VoucherClaimService {
  private readonly destroyRef = inject(DestroyRef);

  private initialized = false;
  private voucherCode: string | null = null;
  private readonly claimingVouchers = new Set<string>();
  private readonly completedVouchers = new Set<string>();
  private readonly failedVouchers = new Set<string>();

  constructor(
    private appService: AppService,
    private route: ActivatedRoute,
    private router: Router,
    private bonusApi: BonusApi,
    private localStorageService: LocalStorageService,
    private localeService: LocaleService,
    private toast: ToastService,
  ) {}

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(queryParams => {
      const voucher = queryParams.get('voucher')?.trim() || null;
      if (voucher === this.voucherCode) return;

      this.voucherCode = voucher;
      this.claimVoucherIfPossible();
    });

    this.appService.userInfo$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(userInfo => {
        if (userInfo) this.claimVoucherIfPossible();
      });
  }

  private claimVoucherIfPossible(): void {
    const voucher = this.voucherCode;
    if (!voucher || !this.localStorageService.loginToken) return;
    if (
      this.claimingVouchers.has(voucher) ||
      this.completedVouchers.has(voucher) ||
      this.failedVouchers.has(voucher)
    ) {
      return;
    }

    this.claimingVouchers.add(voucher);
    this.bonusApi.setExchangeReceive({ exchangeCode: voucher }).subscribe({
      next: response => {
        this.claimingVouchers.delete(voucher);

        if (response === null) {
          // BaseApi already shows the network error toast for null fallbacks.
          this.failedVouchers.add(voucher);
          this.clearVoucherQuery(voucher);
          return;
        }

        this.completedVouchers.add(voucher);
        this.showExchangeResult(response);
        this.clearVoucherQuery(voucher);
      },
      error: () => {
        this.claimingVouchers.delete(voucher);
        this.failedVouchers.add(voucher);
        this.showExchangeFailure();
        this.clearVoucherQuery(voucher);
      },
    });
  }

  private showExchangeResult(response: boolean): void {
    if (response) {
      this.toast.show({
        title: 'Voucher Redeemed',
        message: 'Your voucher was redeemed successfully.Check your transaction history for details.',
        type: 'success',
      });
      return;
    }

    this.toast.show({
      title: 'Unknown error',
      message: 'Please try again',
      type: 'fail',
    });
  }

  private showExchangeFailure(): void {
    this.toast.show({
      message: `${this.localeService.getValue('exchange_fail')}`,
      type: 'fail',
    });
  }

  private clearVoucherQuery(voucher: string): void {
    if (this.voucherCode !== voucher) return;

    void this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { voucher: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .catch(() => undefined);
  }
}
